import { IQuickEditableEntity } from "../components/list/GenericReactGrid";
import { IItemMetadata } from "../components/list/GenericDataGrid";
import { FieldValue } from "./Field";
import { Tool } from "./Tool";
import { UserInformation } from "./UserInformation";
import { VsaComment } from "./VsaComment";
import { FlatVsaaTag } from "./VsaaTag";
import { FieldValues } from "./FieldValues";

export interface ActivityDescription {
  serializedHtml: string;
  imageIds?: string[];
  imageVersionIds?: string[];
  toolLinkIds?: string[];
}

export type ProcessFlowVisibility =
  "currentLevel" | "currentAndChild" | "hidden";
export type ProcessFlowActivityShape = "rectangle";

export interface ProcessFlowSettings {
  visibility: ProcessFlowVisibility;
  activityShape: ProcessFlowActivityShape;
  showArrowToNextStep: boolean;
}

export interface ProcessFlowHeadSettings {
  defaultActivityShape: ProcessFlowActivityShape;
  defaultShowArrowToNextStep: boolean;
}

export const defaultProcessFlowHeadSettings: ProcessFlowHeadSettings = {
  defaultActivityShape: "rectangle",
  defaultShowArrowToNextStep: false,
};

export const defaultProcessFlowSettings: ProcessFlowSettings = {
  visibility: "hidden",
  activityShape: "rectangle",
  showArrowToNextStep: false,
};

export interface Activity extends IItemMetadata, IQuickEditableEntity {
  versionId: string;
  activityId?: string;
  title: string;
  description?: ActivityDescription;
  version?: number;
  publishedBy?: UserInformation;
  publishedTimeInUTC?: string;
  isCurrentVersion: boolean;
  fieldValues?: FieldValues;
  toolIds?: string[];
  tools?: Tool[];
  comments?: VsaComment[];
  mirroredActivityId?: string;
  mirroredChildren?: Activity[];
  processesIdsWithSubactivities?: string[];
  chapters?: FlatVsaaTag[];
  processFlowSettings?: ProcessFlowSettings | null;
  processFlowHeadSettings?: ProcessFlowHeadSettings | null;
}

export interface CreateActivityRequest {
  title: string;
  serializedHtml: string;
  fieldsWithValues?: Record<string, FieldValue>;
  activityId?: string;
  toolIds?: string[];
  mirroredActivityId?: string;
  imageIds?: string[];
  imageVersionIds?: string[];
  toolLinkIds?: string[];
  processFlowSettings?: ProcessFlowSettings | null;
  processFlowHeadSettings?: ProcessFlowHeadSettings | null;
}

export interface UpdateActivityRequest {
  versionId: string;
  title: string;
  serializedHtml: string;
  fieldsWithValues?: Record<string, FieldValue>;
  toolIds?: string[];
  mirroredActivityId?: string | null;
  imageIds?: string[];
  imageVersionIds?: string[];
  toolLinkIds?: string[];
  processFlowSettings?: ProcessFlowSettings | null;
  inheritProcessFlowSettings?: boolean;
  processFlowHeadSettings?: ProcessFlowHeadSettings | null;
}

export interface UpdateActivitiesMetadata {
  versionId: string;
  title?: string;
  fieldsWithValues?: Record<string, FieldValue>;
}
export interface UpdateActivitiesRequest {
  activityMetadata: UpdateActivitiesMetadata[];
}

export interface ActivityFormData {
  title: string;
  richText: string;
  fieldsWithValues?: Record<string, FieldValue>;
  toolIds?: string[];
  mirroredActivityId?: string | null;
  processesIdsWithSubactivities?: string[];
  imageIds?: string[];
  imageVersionIds?: string[];
  toolLinkIds?: string[];
  processFlowSettings?: ProcessFlowSettings | null;
  processFlowHeadSettings?: ProcessFlowHeadSettings | null;
}

export interface PublishActivityRequest {
  versionId: string;
  comment: string;
  hiddenComment: boolean;
}

export interface SaveAndPublishActivityRequest extends UpdateActivityRequest {
  comment: string;
  hiddenComment: boolean;
}

export interface MassPublishActivityRequest {
  versionIds: string[];
  comment: string;
  hiddenComment: boolean;
}

export interface MassPublishActivityResponse {
  results: MassPublishActivityResult[];
  successfulCount: number;
  failedCount: number;
  totalCount: number;
}

export interface MassPublishActivityResult {
  versionId: string;
  success: boolean;
  title?: string;
  publishedActivity?: Activity;
  errorMessage?: string;
  errorCode?: number;
  errorTranslationKey?: string;
}

export interface MassCreateActivityDraftRequest {
  versionIds: string[];
}

export interface MassCreateActivityDraftResponse {
  results: MassCreateActivityDraftResult[];
  successfulCount: number;
  failedCount: number;
  totalCount: number;
}

export interface MassCreateActivityDraftResult {
  versionId: string;
  success: boolean;
  draftActivity?: Activity;
  errorMessage?: string;
  errorCode?: number;
  errorTranslationKey?: string;
}

export interface ActivateActivityRequest {
  versionId: string;
  comment: string;
  hiddenComment: boolean;
  setAsActive: boolean;
}

export const FIELDS_SHOW_PATH = ["process"];
